/**
 * Component sizing tokens.
 *
 * Named widths for overlay surfaces so drawers/panels stay consistent instead
 * of using ad-hoc pixel values. Values are drawn from the observed drawer sizes
 * in the consuming app (380 / 430 / 460 / 560 / 660 / 954px).
 *
 * Control heights are the shared ladder for Button, TextField, Select and any
 * other single-line control that sits in a toolbar or form row. Same `size`
 * name → same outer height, so an inline Button + TextField never disagree by
 * a pixel.
 *
 * Chip has its own `chipHeights` map: `small` is a dense 22px table rung,
 * and `medium` / `large` / `extraLarge` alias onto this ladder so a Chip can
 * sit flush with a Button. Same size *name* on Chip vs Button does not mean
 * the same height — see `chipHeights`.
 */

/** Drawer / side-panel widths (px). */
export const drawerWidths = {
  sm: 380,
  md: 430,
  lg: 560,
  xl: 660,
  full: 954,
} as const;

export type DrawerWidthToken = keyof typeof drawerWidths;

/**
 * Outer height of a single-line control, border included.
 *
 * Button exposes all three. TextField / Select / OutlinedInput only expose
 * `small` and `medium` (MUI has no input `large`), so those map 1:1 onto the
 * first two rungs. Padding is sized under the height rather than the other way
 * around — see `buttonSizes` / `MuiOutlinedInput` in the theme.
 */
export const controlHeights = {
  small: 28,
  medium: 36,
  large: 44,
} as const;

export type ControlHeightToken = keyof typeof controlHeights;

/**
 * Outer height of a Chip, border included.
 *
 * `small` is Chip-only (dense tables). `medium` / `large` / `extraLarge`
 * alias onto `controlHeights` so a Chip can sit flush with a Button:
 * Chip medium ↔ Button small, Chip large ↔ Button medium,
 * Chip extraLarge ↔ Button large. Same size *name* does not mean the
 * same height.
 */
export const chipHeights = {
  small: 22,
  medium: controlHeights.small,
  large: controlHeights.medium,
  extraLarge: controlHeights.large,
} as const;

export type ChipHeightToken = keyof typeof chipHeights;
